"use client";
import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import Image from "next/image";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const CompanyIntegration = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  return (
    <Container
      maxWidth="xxl"
      sx={{
        py: { xs: 0, md: 8 },
        position: "relative",
        direction: isArabic ? "rtl" : "ltr",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage: `url('${IMAGES.BG_WE_DELIVER}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: -1,
          height: "55vh",
          mt: { xs: "1%", sm: "15%", md: "15%" },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: { xs: "350px", sm: "600px", md: "100vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 3, md: 0 },
          mt: { xs: 0, sm: "15%", md: 0 },
        }}
      >
        {/* Desktop Background Image */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "absolute",
            inset: 0,
            zIndex: 0,
          }}
        >
          <Image
            src={
              isArabic
                ? IMAGES.INTEGRATION_45_PLUS_ARABIC
                : IMAGES.INTEGRATION_45_PLUS
            }
            alt="AI recruiter integrates with 45+ HR platforms and ATS systems"
            fill
            style={{ objectFit: "contain" }}
          />
        </Box>

        {/* Mobile Background Image */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "470px",
            zIndex: 0,
          }}
        >
          <Image
            src={IMAGES.MOBILE_BG_INTEGRATION}
            alt="AI recruiter integrates with 45+ HR platforms and ATS systems"
            fill
            style={{ objectFit: "contain" }}
          />
        </Box>

        {/* Center Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            maxWidth: { xs: "90%", sm: "80%", md: "600px" },
            px: { xs: 2, sm: 3, md: 0 },
            mx: "auto",
            mt: { xs: "10%", sm: "-30%", md: 0 },
          }}
        >
          {/* Badge */}
          <Box
            sx={{
              display: "inline-block",
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.8, sm: 0.8 },
              border: "1px solid #DDE4E8",
              borderRadius: "20px",
              mb: { xs: 3, sm: 3, md: 3 },
              backgroundColor: "white",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#647482",
                letterSpacing: "0.5px",
                fontWeight: 500,
                fontFamily: "var(--font-plusjakarta)",
                fontSize: { xs: "12px", sm: "11px", md: "14px" },
              }}
            >
              {t("companyIntegration.badge")}
            </Typography>
          </Box>

          {/* Heading */}
          {[
            { key: "heading1", color: "#020617" },
            { key: "heading2", color: "#775CE5" },
            { key: "heading3", color: "#020617" },
          ].map((item, i) => (
            <Typography
              key={i}
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                mb:
                  i === 2
                    ? { xs: 2, sm: 1.5, md: 2 }
                    : { xs: 1, sm: i === 0 ? 1 : 1.5, md: i === 0 ? 1 : 2 },
                color: item.color,
                fontSize: {
                  xs: "1.1rem",
                  sm: "1.3rem",
                  md: "2rem",
                  lg: "2rem",
                  xl: "2.5rem",
                },
                lineHeight: { xs: 1, sm: 1, md: 1.2 },
                fontFamily: "var(--font-plusjakarta)",
                "@media (min-width: 900px) and (max-width: 1200px)": {
                  fontSize: "1.8rem",
                },
              }}
            >
              {t(`companyIntegration.${item.key}`)}
            </Typography>
          ))}

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: "#2A2F4699",
              mb: { xs: 2, sm: 3, md: 4 },
              fontSize: {
                xs: "0.6rem",
                sm: "0.8rem",
                md: "1rem",
                lg: "0.9rem",
                xl: "1.3rem",
              },
              lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
              fontFamily: "var(--font-outfit)",
              fontWeight: 400,
              maxWidth: { xs: 800, sm: 800, md: 800, lg: 400, xl: 800 },
              mx: "auto",
              "@media (min-width: 900px) and (max-width: 1200px)": {
                fontSize: "0.8rem",
                maxWidth: 350,
              },
            }}
          >
            {t("companyIntegration.desc")}
          </Typography>

          {/* Button */}
          <Button
            variant="contained"
            href="/free-trial-of-ai-recruiting-tool"
            sx={{
              background: "#9048FB",
              color: "white",
              px: { xs: 1.8, sm: 2.5, md: 3 },
              py: { xs: 0.5, sm: 1, md: 1.2 },
              borderRadius: "25px",
              textTransform: "none",
              fontFamily: "var(--font-outfit)",
              fontSize: { xs: "0.6rem", sm: "0.8rem", md: "1rem" },
              fontWeight: 600,
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              mx: "auto",
              width: "fit-content", // ✅ yeh add karo
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                background: "#000",
                boxShadow: "none",
                transform: "scale(1.05) translateY(-2px)",
              },
              "@media (min-width: 900px) and (max-width: 1200px)": {
                fontSize: "0.7rem",
              },
            }}
            endIcon={
              <ArrowForwardIcon
                sx={{
                  fontSize: { xs: 13, sm: 17, md: 20 },
                  transform: isArabic ? "scaleX(-1)" : "none",
                }}
              />
            }
          >
            {t("companyIntegration.cta")}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default CompanyIntegration;
